import {
  Grid,
  IconButton,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  LinearProgress,
} from "@mui/material";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useSessao } from "../../redux/sessao/sessaoHooks";
import { useSnack } from "../../redux/snack/snackHooks";
import { makeStyles } from "@material-ui/core";
import { Usuario } from "../../model/entities/Usuario";
import agendamentoApi from "../../api/agendamentoApi";
import TextFieldGeneric from "../../components/Generics/TextFieldGeneric";
import { useEmpresa } from "../../redux/empresa/empresaHooks";
import ModalGeneric from "../../components/Generics/ModalGeneric";
import UsuarioField from "../../components/fields/UsuarioField";
import CloseIcon from "@mui/icons-material/Close";
import timezone from "dayjs/plugin/timezone"; // Importa o plugin de timezone
import utc from "dayjs/plugin/utc";
import DateTimePickerGeneric from "../../components/Generics/DateTimePicker";

dayjs.extend(timezone); // Extende o dayjs com o plugin timezone
dayjs.extend(utc);

type Props = {
  dataAgendamento?: string;
  onReload?: () => void;
  idAgendamento?: number;
  onClose?: () => void;
};

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(4),
    borderRadius: "15px",
    background: "rgba(255, 255, 255, 0.9)",
    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
    maxWidth: "800px",
    margin: "auto",
  },
  header: {
    marginBottom: theme.spacing(2),
    textAlign: "center",
  },
  button: {
    marginTop: theme.spacing(2),
  },
  iconButton: {
    display: "flex",
    alignItems: "center",
    marginTop: theme.spacing(2),
  },
  meetIcon: {
    maxWidth: "25px",
    padding: "1px",
    marginRight: theme.spacing(1),
  },
}));

const FormularioAgenda: React.FC<Props> = (props) => {
  const { onReload, idAgendamento, onClose } = props;
  const mostrarSnack = useSnack();
  const [sessao] = useSessao();
  const [titulo, setTitulo] = useState("");

  const [solucionado, setSolucionado] = useState(false);
  const [loading, setLoading] = useState(false);
  const [horaInicio, setHoraInicio] = useState("00:00");
  const [horaFim, setHoraFim] = useState("23:59");
  const [observacao, setObservaccao] = useState("");
  const [prioridade, setPrioridade] = useState(0);

  const [dataInicial, setDataInicial] = useState(dayjs().toDate());
  const [dataFinal, setDataFinal] = useState(dayjs().toDate());

  const [empresa] = useEmpresa();
  const [agendadorPor, setAgendadorPor] = useState<Usuario | null>(
    sessao.usuario
  );
  const [agendarPara, setAgendarPara] = useState<Usuario | null>(
    sessao.usuario
  );

  const schema = Yup.object().shape({
    titulo: Yup.string().required("Informe o título"),
    agendarPara: Yup.number().required("Informe para quem será o agendamento"),
    agendadorPor: Yup.number().required(
      "Informe quem está fazendo o agendamento"
    ),
  });

  const {
    register,
    handleSubmit: handleSubmitForm,
    watch,
    control,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  const handleSubmit = async (data: any) => {
    if (Object.keys(errors).length === 0) {
      await salvar();
    } else {
      console.error("Houve erros de validação:", errors);
    }
  };

  useEffect(() => {
    buscarAgendamento();
  }, []);

  async function buscarAgendamento() {
    try {
      setLoading(true);
      if (idAgendamento) {
        const res = await agendamentoApi.getById(Number(idAgendamento));
        setTitulo(res.title);
        setDataInicial(res.startDate);
        setDataFinal(res.endDate);
        setSolucionado(res.solucionado);
        setObservaccao(res.observacao);
        setHoraInicio(dayjs(res.startDate).format("HH:mm"));
        setHoraFim(dayjs(res.endDate).format("HH:mm"));
        setAgendadorPor(res.agendadoPor);
        setAgendarPara(res.agendadoPara);
        setPrioridade(res.prioridade);
      }
    } catch (erro: any) {
      console.log("erro ao buscar agendamento especifico", erro);
      mostrarSnack("Erro ao buscar dados do agendamento", "error");
    }
    setLoading(false);
  }

  function formatToISOString(date: Date, hour: number, minute: number) {
    const updatedDate = dayjs(date)
      .utc()
      .set("hour", hour)
      .set("minute", minute)
      .set("second", 0)
      .set("millisecond", 0);

    return updatedDate.toISOString();
  }

  const salvar = async () => {
    try {
      if (!titulo) {
        mostrarSnack("Por favor informe o título", "error");
        return;
      }

      if (!agendadorPor || !agendadorPor.id) {
        mostrarSnack(
          "Por favor informe quem está fazendo o agendamento",
          "error"
        );
        return;
      }

      if (!agendarPara || !agendarPara.id) {
        mostrarSnack("Por favor informe para quem é o agendamento", "error");
        return;
      }
      setLoading(true);

      const horaInicial = dataInicial.getHours();
      const minutoInicial = dataInicial.getMinutes();

      const horaFinal = dataFinal.getHours();
      const minutoFinal = dataFinal.getHours();

      const startDateTimeZone = formatToISOString(
        dataInicial,
        Number(horaInicial),
        Number(minutoInicial)
      );

      const endDateTimezone = formatToISOString(
        dataFinal,
        Number(horaFinal),
        Number(minutoFinal)
      );

      if (idAgendamento) {
        await agendamentoApi.update(idAgendamento, {
          allDay: false,
          title: titulo,
          endDate: endDateTimezone,
          startDate: dataInicial.toDateString(),
          horaFim: horaFim,
          horaInicio: horaInicio,
          id: 0,
          observacao: observacao,
          prioridade: prioridade,
          solucionado: solucionado,
          agendadoPara: agendarPara?.id,
          agendadoPor: agendadorPor?.id,
        });
        setLoading(false);
        mostrarSnack("Agendamento atualizado!", "success");
        if (onReload) onReload();
        return;
      } else {
        await agendamentoApi.insert({
          allDay: false,
          title: titulo,
          endDate: endDateTimezone,
          startDate: startDateTimeZone,
          horaFim: horaFim,
          horaInicio: horaInicio,
          id: 0,
          observacao: observacao,
          prioridade: prioridade,
          solucionado: solucionado,
          agendadoPara: agendarPara?.id,
          agendadoPor: agendadorPor?.id,
        });

        mostrarSnack("Agendamento concluído!", "success");
        if (onReload) onReload();
        if (onClose) onClose();
        setLoading(false);
      }
    } catch (erro: any) {
      mostrarSnack("Não foi possível criar agendamento", "error");
      console.log("Não foi possível criar agendamento", erro);
      setLoading(false);
    }
  };

  const classes = useStyles();

  return (
    <Paper className={classes.container}>
      <IconButton
        onClick={onClose}
        style={{
          height: 53,
          width: 53,
          position: "absolute",
          top: 0,
          right: 0,
        }}
      >
        <CloseIcon />
      </IconButton>

      {loading && (
        <ModalGeneric
          open={loading}
          onClose={() => {}}
          style={{ background: "transparent" }}
        >
          <h3 style={{ textAlign: "center", margin: "15px" }}>
            {empresa?.utilizaIntegracaoCalendar
              ? "Comunicando com a central do Google Calendar"
              : "Gravando agendamento"}
          </h3>
          <LinearProgress />
        </ModalGeneric>
      )}

      <form onSubmit={handleSubmitForm(handleSubmit)}>
        <Box className={classes.header}>
          <Typography variant="h5">
            {idAgendamento ? "Editar agendamento" : "Criar novo agendamento"}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              name="titulo"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label={"Título"}
                  placeholder="Adicionar um título"
                  value={titulo}
                  error={!!errors.titulo}
                  helperText={errors.titulo ? errors.titulo.message : ""}
                  onChange={(e) => {
                    field.onChange(e);
                    setTitulo(e.target.value);
                  }}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <DateTimePickerGeneric
              label={"Data início"}
              value={dataInicial}
              minTime={new Date(0, 0, 0, 8, 0)}
              maxTime={new Date(0, 0, 0, 18, 0)}
              setValue={setDataInicial}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <DateTimePickerGeneric
              label={"Data fim"}
              value={dataFinal}
              minTime={new Date(0, 0, 0, 8, 0)}
              maxTime={new Date(0, 0, 0, 18, 0)}
              setValue={setDataFinal}
            />
          </Grid>

          <Grid item xs={12}>
            <TextFieldGeneric
              fullWidth
              label={"Descrição"}
              placeholder="Adicionar uma descrição"
              value={observacao}
              minRows={4}
              maxLength={150}
              onChange={(e) => setObservaccao(e)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="agendadorPor"
              control={control}
              render={({ field }) => (
                <UsuarioField
                  idUsuario={agendadorPor?.id}
                  label={"Agendador por"}
                  usuario={agendadorPor}
                  setUsuario={(u) => {
                    field.onChange(u?.id);
                    setAgendadorPor(u);
                  }}
                  error={!!errors.agendadorPor}
                  helperText={
                    errors.agendadorPor ? errors.agendadorPor.message : ""
                  }
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="agendarPara"
              control={control}
              render={({ field }) => (
                <UsuarioField
                  label={"Agendar para"}
                  usuario={agendarPara}
                  idUsuario={agendarPara?.id}
                  setUsuario={(u) => {
                    field.onChange(u?.id);
                    setAgendarPara(u);
                  }}
                  error={!!errors.agendarPara}
                  helperText={
                    errors.agendarPara ? errors.agendarPara.message : ""
                  }
                />
              )}
            />
          </Grid>

          <Grid item xs={12} style={{ textAlign: "center" }}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              className={classes.button}
            >
              Salvar
            </Button>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default FormularioAgenda;
